import React, { useState, useMemo, useEffect } from 'react';
import FundraisingSimulationClient from '../../../models/fundraisingSimulations/fundraisingSimulationClient';
import type { ResultData } from '../../../models/fundraisingSimulations/resultData';

import FundinnoImg01 from '../../../../../assets/images/views/lps/fundraising_sim/fun_img01.png';
import FundinnoImgSp01 from '../../../../../assets/images/views/lps/fundraising_sim/fun_img-sp01.png';
import FundinnoImg02 from '../../../../../assets/images/views/lps/fundraising_sim/fun_img02.png';
import FundinnoImgSP02 from '../../../../../assets/images/views/lps/fundraising_sim/fun_img-sp02.png';

import PlusImg01 from '../../../../../assets/images/views/lps/fundraising_sim/plus_img01.png';
import PlusImgSp01 from '../../../../../assets/images/views/lps/fundraising_sim/plus_img-sp01.png';
import PlusImg02 from '../../../../../assets/images/views/lps/fundraising_sim/plus_img02.png';
import PlusImgSP02 from '../../../../../assets/images/views/lps/fundraising_sim/plus_img-sp02.png';
import { handleAxiosError } from '../../../../common/helpers/axiosHelper';
import { simpleFlashMessage } from '../../../../common/utils/flashMessage';

export type Option = [string, string];

export type Tag = {
  id: number;
  name: string;
  category: string;
  [key: string]: string | number; // tagSortKeyの動的プロパティ用
};

export type FormViewProps = {
  requestedFundingAmountOptions: Array<Option>;
  totalFundingAmountOptions: Array<Option>;
  currentValuationOptions: Array<Option>;
  previousPeriodSalesOptions: Array<Option>;
  financialStatusOptions: Array<Option>;
};

type Props = FormViewProps & {
  type: string;
  tagSortKey: string;
  presidentAgeOptions: Array<Option>;
  birthOnOptions: Array<Option>;
  businessTypeOptions: Array<Option>;
  fundraisingSimulationTags: Array<Tag>;
  onToggleAccordion: () => void;
  onCloseAccordion: () => void;
  isOpen: boolean;
};

type SelectProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<Option>;
  error: string | undefined;
};

type TagButtonProps = {
  label: string;
  tags: Array<Tag>;
  category: string;
  disabled: boolean;
  selectedTagIds: Array<number>;
  onChange: (value: number) => void;
  onReset: () => void;
  error: string | undefined;
};

type FormData = {
  presidentAge: string;
  birthOn: string;
  businessType: string;
  requestedFundingAmount: string;
  totalFundingAmount: string;
  currentValuation: string;
  previousPeriodSales: string;
  financialStatus: string;
};

type LastSentData = {
  formData: FormData;
  selectedTagIds: Array<number>;
};

type Errors = {
  presidentAge?: string;
  birthOn?: string;
  businessType?: string;
  requestedFundingAmount?: string;
  totalFundingAmount?: string;
  currentValuation?: string;
  previousPeriodSales?: string;
  financialStatus?: string;
  presidentTag?: string;
  employeeTag?: string;
  companyStatusTag?: string;
  financialStatusTag?: string;
  shareholderTag?: string;
};

const FormView = (props: Props) => {
  const fundraisingSimulationClient = new FundraisingSimulationClient();

  const [formData, setFormData] = useState<FormData>({
    presidentAge: '',
    birthOn: '',
    businessType: '',
    requestedFundingAmount: '',
    totalFundingAmount: '',
    currentValuation: '',
    previousPeriodSales: '',
    financialStatus: '',
  });
  const [selectedTagIds, setSelectedTagIds] = useState<Array<number>>([]);
  const [disabledCategory, setDisabledCategory] = useState<Array<string>>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [lastSentData, setLastSentData] = useState<LastSentData>({
    formData: {
      presidentAge: '',
      birthOn: '',
      businessType: '',
      requestedFundingAmount: '',
      totalFundingAmount: '',
      currentValuation: '',
      previousPeriodSales: '',
      financialStatus: '',
    },
    selectedTagIds: [],
  });
  const [resultData, setResultData] = useState<ResultData>({
    score: 0,
    simulatedAmount: null,
    unit: null,
    range: null,
  });
  const [loading, setLoading] = useState<boolean>(false); // 二重送信防止

  useEffect(() => {
    const cachedData = JSON.parse(sessionStorage.getItem(props.type) || '{}');
    const cachedFormData = cachedData['formData'] as FormData;
    const cachedSelectedTagIds = cachedData['selectedTagIds'] as Array<number>;
    const cachedDisabledCategory = cachedData[
      'disabledCategory'
    ] as Array<string>;
    const cachedResultData = cachedData['resultData'] as ResultData;

    if (cachedFormData) setFormData(cachedFormData);
    if (cachedSelectedTagIds) setSelectedTagIds(cachedSelectedTagIds);
    if (cachedDisabledCategory) setDisabledCategory(cachedDisabledCategory);
    if (cachedResultData) setResultData(cachedResultData);
  }, [props.type]);

  //共通化:タグの選択肢
  const getSortedTagsByCategory = (category: string) => {
    return useMemo(() => {
      return (
        props.fundraisingSimulationTags
          .filter(tag => tag.category === category)
          .filter(tag => tag[props.tagSortKey] != null)
          // eslint-disable-next-line
          // @ts-ignore
          .sort((a, b) => a[props.tagSortKey] - b[props.tagSortKey])
      );
    }, [props.fundraisingSimulationTags, props.tagSortKey]);
  };

  const presidentTag = getSortedTagsByCategory('1');
  const employeeTag = getSortedTagsByCategory('2');
  const companyStatusTag = getSortedTagsByCategory('3');
  const financialStatusTag = getSortedTagsByCategory('4');
  const shareholderTag = getSortedTagsByCategory('5');

  const handleChange = (key: keyof Errors, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));

    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors[key]) {
        delete newErrors[key];
      }
      return newErrors;
    });
  };

  const toggleSelection = (key: keyof Errors, id: number) => {
    setSelectedTagIds(
      prevSelected =>
        prevSelected.includes(id)
          ? prevSelected.filter(selectedId => selectedId !== id) // すでに選ばれてたら除外
          : [...prevSelected, id], // なければ追加
    );

    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors[key]) {
        delete newErrors[key];
      }
      return newErrors;
    });
  };

  const handleResetTags = (tags: Array<Tag>, key: keyof Errors) => {
    const tagIdsToRemove = tags.map(tag => tag.id);
    setSelectedTagIds(prev => prev.filter(id => !tagIdsToRemove.includes(id)));

    setDisabledCategory(prev =>
      prev.includes(key)
        ? prev.filter(disabledCategory => disabledCategory !== key)
        : [...prev, key],
    );

    setErrors((prevErrors: Errors): Errors => {
      const newErrors = { ...prevErrors };
      if (newErrors[key]) {
        delete newErrors[key];
      }
      return newErrors;
    });
  };

  const checkCategorySelected = (
    categoryTagList: Array<Tag>,
    key: keyof Errors,
  ): boolean => {
    return (
      !categoryTagList.some(tag => selectedTagIds.includes(tag.id)) &&
      !disabledCategory.includes(key)
    );
  };

  const validate = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.presidentAge)
      newErrors.presidentAge = '年齢を選択してください';
    if (!formData.birthOn) newErrors.birthOn = '設立年を選択してください';
    if (!formData.businessType)
      newErrors.businessType = '業界・業種を選択してください';
    if (!formData.requestedFundingAmount)
      newErrors.requestedFundingAmount = '調達希望金額を選択してください';
    if (!formData.totalFundingAmount)
      newErrors.totalFundingAmount = '累計資金調達総額を選択してください';
    if (!formData.currentValuation)
      newErrors.currentValuation = '現在バリュエーションを選択してください';
    if (!formData.previousPeriodSales)
      newErrors.previousPeriodSales = '前期売上高を選択してください';
    if (!formData.financialStatus)
      newErrors.financialStatus = '業績・財務を選択してください';

    if (checkCategorySelected(presidentTag, 'presidentTag'))
      newErrors.presidentTag = '経験・実績を選択してください';
    if (checkCategorySelected(employeeTag, 'employeeTag'))
      newErrors.employeeTag = '経験・実績を選択してください';
    if (checkCategorySelected(companyStatusTag, 'companyStatusTag'))
      newErrors.companyStatusTag = 'ステータス・特徴を選択してください';
    if (checkCategorySelected(financialStatusTag, 'financialStatusTag'))
      newErrors.financialStatusTag = 'IPO計画を選択してください';
    if (checkCategorySelected(shareholderTag, 'shareholderTag'))
      newErrors.shareholderTag = '資金調達実績を選択してください';

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // 返却パラメータの加工用
  const toNumOrNull = (value: string): number | null => {
    const num = Number(value);
    return isNaN(num) || value === '' ? null : num;
  };

  const handleSimulate = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (loading) return;
    if (!validate()) return;

    setLoading(true);

    fundraisingSimulationClient
      .simulate({
        type: props.type,
        fundraising_simulation: {
          president_age: toNumOrNull(formData.presidentAge),
          birth_on: toNumOrNull(formData.birthOn),
          business_type: toNumOrNull(formData.businessType),
          requested_funding_amount: toNumOrNull(
            formData.requestedFundingAmount,
          ),
          total_funding_amount: toNumOrNull(formData.totalFundingAmount),
          current_valuation: toNumOrNull(formData.currentValuation),
          previous_period_sales: toNumOrNull(formData.previousPeriodSales),
          financial_status: toNumOrNull(formData.financialStatus),
        },
        fundraising_simulation_tag: {
          ids: selectedTagIds,
        },
      })
      .then(response => {
        setResultData({ ...response.data });
        setLastSentData({
          formData: { ...formData },
          selectedTagIds: [...selectedTagIds],
        });

        // 戻る対策でSessionにキャッシュ
        sessionStorage.setItem(
          props.type,
          JSON.stringify({
            formData,
            selectedTagIds,
            disabledCategory,
            resultData: { ...response.data },
          }),
        );
      })
      .catch(error => {
        handleAxiosError(error, simpleFlashMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (loading) return; // 二重送信防止
    setLoading(true);

    // シミュレーション結果を保存する
    fundraisingSimulationClient
      .create({
        type: props.type,
        fundraising_simulation: {
          president_age: toNumOrNull(lastSentData.formData.presidentAge),
          birth_on: toNumOrNull(lastSentData.formData.birthOn),
          business_type: toNumOrNull(lastSentData.formData.businessType),
          requested_funding_amount: toNumOrNull(
            lastSentData.formData.requestedFundingAmount,
          ),
          total_funding_amount: toNumOrNull(
            lastSentData.formData.totalFundingAmount,
          ),
          current_valuation: toNumOrNull(
            lastSentData.formData.currentValuation,
          ),
          previous_period_sales: toNumOrNull(
            lastSentData.formData.previousPeriodSales,
          ),
          financial_status: toNumOrNull(lastSentData.formData.financialStatus),
        },
        fundraising_simulation_tag: {
          ids: lastSentData.selectedTagIds,
        },
      })
      .finally(() => {
        // ユーザの見た目上はリンクのため、apiの結果を問わず遷移はする
        const urlParams = new URLSearchParams({ ref: 'sim' });
        window.location.href = `/company_contact?${urlParams.toString()}`;
      });
  };

  const SelectField = ({
    id,
    label,
    value,
    onChange,
    options,
    error,
  }: SelectProps) => (
    <div className="simulator__sectiom__item select-block">
      <label
        htmlFor={`${props.type}-${id}`}
        className="simulator__sectiom__item-title required"
      >
        {label}
      </label>
      <div className="simulator__sectiom__item-cont select-block">
        <div className={`simulator__select ${error ? 'is-invalid' : ''}`}>
          <select
            id={`${props.type}-${id}`}
            value={value}
            onChange={e => onChange(e.target.value)}
            className={value === '' ? 'is-empty' : ''}
          >
            <option value="">選択してください</option>
            {options.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );

  const TagButton = ({
    label,
    tags,
    category,
    disabled,
    selectedTagIds,
    onChange,
    onReset,
    error,
  }: TagButtonProps) => (
    <div className="simulator__sectiom__item">
      <div className="simulator__sectiom__item-title required">
        {label}
        <span>(複数選択可)</span>
      </div>
      <div className="simulator__sectiom__item-cont check-block">
        <fieldset
          className={`simulator__checkbox ${error ? 'is-invalid' : ''}`}
        >
          {tags.map(tag => (
            <label
              key={tag.id}
              htmlFor={`${props.type}-checkbox-${tag.id}`}
              className={disabled ? 'disabled' : ''}
            >
              {tag.name}
              <input
                type="checkbox"
                id={`${props.type}-checkbox-${tag.id}`}
                checked={selectedTagIds.includes(tag.id)}
                onChange={() => onChange(tag.id)}
                disabled={disabled}
              />
            </label>
          ))}
          <label htmlFor={`${props.type}-reset-${category}`}>
            当てはまる項目がない
            <input
              type="checkbox"
              id={`${props.type}-reset-${category}`}
              checked={disabled}
              onChange={() => onReset()}
            />
          </label>
        </fieldset>
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );

  return (
    <div
      className={`simulator__block ${
        props.type === 'ecf' ? 'blue' : props.type === 'plus' ? 'black' : ''
      }`}
      onClick={props.onToggleAccordion}
    >
      {props.type == 'ecf' && (
        <h3>
          <span className="consider-text">
            <img
              className="sp-none"
              src={FundinnoImg01}
              width="351"
              height="30"
            />
            <img
              className="pc-none"
              src={FundinnoImgSp01}
              width="224"
              height="22"
            />
          </span>
          <span className="consider-text">
            <img
              className="sp-none"
              src={FundinnoImg02}
              width="535"
              height="48"
            />
            <img
              className="pc-none"
              src={FundinnoImgSP02}
              width="287"
              height="28"
            />
          </span>
        </h3>
      )}

      {props.type == 'plus' && (
        <h3>
          <span className="consider-text">
            <img className="sp-none" src={PlusImg01} width="220" height="30" />
            <img
              className="pc-none"
              src={PlusImgSp01}
              width="140"
              height="22"
            />
          </span>
          <span className="consider-text">
            <img className="sp-none" src={PlusImg02} width="535" height="48" />
          </span>
          <span className="consider-text">
            <img
              className="pc-none"
              src={PlusImgSP02}
              width="287"
              height="28"
            />
          </span>
        </h3>
      )}

      <div
        className={`simulator__contents ${
          props.isOpen ? '' : 'accordion-close'
        }`}
        style={props.isOpen ? {} : { height: '285px', display: 'none' }}
      >
        {Object.keys(errors).length > 0 && (
          <div className="simulator__contents--error">
            未入力の必須項目があります。
          </div>
        )}
        <div className="simulator__sectiom">
          <h4>代表について</h4>
          <SelectField
            id="presidentAge"
            label="年齢"
            value={formData.presidentAge}
            onChange={value => handleChange('presidentAge', value)}
            options={props.presidentAgeOptions}
            error={errors.presidentAge}
          />
          <TagButton
            label="経験・実績"
            tags={presidentTag}
            category="1"
            disabled={disabledCategory.includes('presidentTag')}
            selectedTagIds={selectedTagIds}
            onChange={value => toggleSelection('presidentTag', value)}
            onReset={() => handleResetTags(presidentTag, 'presidentTag')}
            error={errors.presidentTag}
          />
        </div>
        <div className="simulator__sectiom">
          <h4>経営陣/幹部について</h4>
          <TagButton
            label="経験・実績"
            tags={employeeTag}
            category="2"
            disabled={disabledCategory.includes('employeeTag')}
            selectedTagIds={selectedTagIds}
            onChange={value => toggleSelection('employeeTag', value)}
            onReset={() => handleResetTags(employeeTag, 'employeeTag')}
            error={errors.employeeTag}
          />
        </div>
        <div className="simulator__sectiom">
          <h4>設立年について</h4>
          <SelectField
            id="birthOn"
            label="設立年"
            value={formData.birthOn}
            onChange={value => handleChange('birthOn', value)}
            options={props.birthOnOptions}
            error={errors.birthOn}
          />
        </div>
        <div className="simulator__sectiom">
          <h4>事業について</h4>
          <SelectField
            id="businessType"
            label="業界・業種"
            value={formData.businessType}
            onChange={value => handleChange('businessType', value)}
            options={props.businessTypeOptions}
            error={errors.businessType}
          />
          <TagButton
            label="ステータス・特徴"
            tags={companyStatusTag}
            category="3"
            disabled={disabledCategory.includes('companyStatusTag')}
            selectedTagIds={selectedTagIds}
            onChange={value => toggleSelection('companyStatusTag', value)}
            onReset={() =>
              handleResetTags(companyStatusTag, 'companyStatusTag')
            }
            error={errors.companyStatusTag}
          />
        </div>
        <div className="simulator__sectiom">
          <h4>ファイナンスについて</h4>
          <SelectField
            id="requestedFundingAmount"
            label="調達希望金額"
            value={formData.requestedFundingAmount}
            onChange={value => handleChange('requestedFundingAmount', value)}
            options={props.requestedFundingAmountOptions}
            error={errors.requestedFundingAmount}
          />
          <SelectField
            id="totalFundingAmount"
            label="累計調達額"
            value={formData.totalFundingAmount}
            onChange={value => handleChange('totalFundingAmount', value)}
            options={props.totalFundingAmountOptions}
            error={errors.totalFundingAmount}
          />
          <SelectField
            id="currentValuation"
            label="現在バリュエーション"
            value={formData.currentValuation}
            onChange={value => handleChange('currentValuation', value)}
            options={props.currentValuationOptions}
            error={errors.currentValuation}
          />
          <SelectField
            id="previousPeriodSales"
            label="前期売上高"
            value={formData.previousPeriodSales}
            onChange={value => handleChange('previousPeriodSales', value)}
            options={props.previousPeriodSalesOptions}
            error={errors.previousPeriodSales}
          />
          <SelectField
            id="financialStatus"
            label="業績・財務"
            value={formData.financialStatus}
            onChange={value => handleChange('financialStatus', value)}
            options={props.financialStatusOptions}
            error={errors.financialStatus}
          />
          <TagButton
            label="IPO計画"
            tags={financialStatusTag}
            category="4"
            disabled={disabledCategory.includes('financialStatusTag')}
            selectedTagIds={selectedTagIds}
            onChange={value => toggleSelection('financialStatusTag', value)}
            onReset={() =>
              handleResetTags(financialStatusTag, 'financialStatusTag')
            }
            error={errors.financialStatusTag}
          />
          <TagButton
            label="資金調達実績"
            tags={shareholderTag}
            category="5"
            disabled={disabledCategory.includes('shareholderTag')}
            selectedTagIds={selectedTagIds}
            onChange={value => toggleSelection('shareholderTag', value)}
            onReset={() => handleResetTags(shareholderTag, 'shareholderTag')}
            error={errors.shareholderTag}
          />
        </div>

        <a className="diagnosis-result-btn" onClick={handleSimulate}>
          診断結果をみる
        </a>
        {resultData.simulatedAmount !== null && (
          <div
            className={
              resultData.simulatedAmount === 0
                ? 'diagnosis-result--none'
                : 'diagnosis-result'
            }
          >
            <div className="score-text">診断結果</div>
            {resultData.simulatedAmount === 0 ? (
              <div className="score-text-wrap">
                <p>
                  大変申し訳ございませんが、現在、弊社の
                  <br className="pc-none" />
                  データ不足で表示できません。
                </p>
                <p>
                  資金調達の可能性も含め、
                  <br className="pc-none" />
                  お気軽にご相談ください。
                </p>
              </div>
            ) : (
              <>
                {props.type === 'ecf' && (
                  <p>
                    株式投資型
                    <br className="pc-none" />
                    クラウドファンディングを活用して
                  </p>
                )}
                {props.type === 'plus' && (
                  <p>
                    大型資金調達
                    <br className="pc-none" />
                    サービスを活用して
                  </p>
                )}
                <div className="value-block">
                  <div className="value">
                    {resultData.simulatedAmount.toLocaleString()}
                  </div>
                  <span className={props.type === 'plus' ? 'plus-unit' : ''}>
                    {`${resultData.unit}円${resultData.range}`}
                  </span>
                </div>
                <p>
                  の資金調達をご支援できる
                  <br className="pc-none" />
                  可能性があります
                </p>
              </>
            )}
            <a href="/company_contact" onClick={handleSubmit}>
              今すぐ資金調達の相談をする
            </a>
            {resultData.simulatedAmount !== 0 && (
              <p className="note-text">
                ※ 本シミュレーション結果は、参考情報として提供するものであり、
                <br className="sp-none" />
                資金調達の実現や調達金額、条件等を保証・約束するものではありません。
              </p>
            )}
          </div>
        )}
        <div className="simulator__contents__close">
          <span
            className="simulator__contents__close-btn"
            onClick={e => {
              e.stopPropagation();
              props.onCloseAccordion();
            }}
          >
            ×質問を閉じる
          </span>
        </div>
      </div>
      {!props.isOpen && (
        <div className="simulator__overlay">
          <div className="accordion-open">
            <div className="accordion-open-icon" />
            <div className="accordion-open-text">診断スタート</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormView;
